import { useEffect, useRef, useState } from 'react';
import Logo from '@/components/ui/Logo';

export interface CidadeOpcao {
  chave: string;
  rotulo: string;
}

interface Props {
  logado: boolean;
  nome: string;
  saudacao: string;
  cidades: CidadeOpcao[];
  cidade: string;
  aoEscolherCidade: (chave: string) => void;
  aoAbrirMenu: () => void;
}

// ---------------------------------------------------------------------------
// Topo da página inicial (celular): marca à esquerda, filtro de cidade e ☰ à
// direita. Nada de campo de busca aqui — a busca mora na Agenda. Quem está
// logado vê o nome pequeno embaixo da marca.
// ---------------------------------------------------------------------------
export default function TopoInicio({
  logado,
  nome,
  saudacao,
  cidades,
  cidade,
  aoEscolherCidade,
  aoAbrirMenu,
}: Props) {
  const [listaAberta, setListaAberta] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!listaAberta) return;
    const fechar = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) setListaAberta(false);
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [listaAberta]);

  const atual = cidades.find((c) => c.chave === cidade);
  const rotuloCidade = atual ? atual.rotulo : 'Todas';

  return (
    <header className="md:hidden">
      <div className="mx-auto flex w-full max-w-[720px] items-center justify-between gap-3 px-4 pt-3">
        <div className="flex min-w-0 items-center gap-2">
          <Logo imgClassName="h-9 w-auto object-contain" />
          <div className="min-w-0">
            <p className="truncate text-[14px] font-extrabold leading-tight text-foreground-950">
              Se Liga Católico
            </p>
            {logado && nome ? (
              <p className="truncate text-[12px] leading-tight text-foreground-600">
                {saudacao} {nome}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <div ref={ref} className="relative">
            <button
              type="button"
              onClick={() => setListaAberta((v) => !v)}
              aria-label="Filtrar por cidade"
              aria-expanded={listaAberta}
              className="aperta flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 text-[12.5px] font-bold text-foreground-800"
            >
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-map-pin-2-line text-[14px]" aria-hidden />
              </span>
              <span className="max-w-[86px] truncate">{rotuloCidade}</span>
              <i
                className={`ri-arrow-down-s-line text-[14px] text-foreground-500 transition-transform ${
                  listaAberta ? 'rotate-180' : ''
                }`}
                aria-hidden
              />
            </button>

            {listaAberta && (
              <div className="animate-subir absolute right-0 top-full z-50 mt-2 w-[190px] overflow-hidden rounded-[12px] border border-background-200 bg-background-50 py-1">
                <p className="px-3 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-wider text-foreground-500">
                  Cidade
                </p>
                <button
                  type="button"
                  onClick={() => {
                    aoEscolherCidade('');
                    setListaAberta(false);
                  }}
                  className={`flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-[14px] font-semibold transition-colors hover:bg-background-100 ${
                    cidade === '' ? 'text-primary-700' : 'text-foreground-800'
                  }`}
                >
                  Todas
                  {cidade === '' && <i className="ri-check-line text-[16px]" aria-hidden />}
                </button>
                {cidades.map((c) => (
                  <button
                    key={c.chave}
                    type="button"
                    onClick={() => {
                      aoEscolherCidade(c.chave);
                      setListaAberta(false);
                    }}
                    className={`flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-[14px] font-semibold transition-colors hover:bg-background-100 ${
                      cidade === c.chave ? 'text-primary-700' : 'text-foreground-800'
                    }`}
                  >
                    <span className="truncate">{c.rotulo}</span>
                    {cidade === c.chave && (
                      <i className="ri-check-line text-[16px]" aria-hidden />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={aoAbrirMenu}
            aria-label="Menu"
            className="aperta flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground-800"
          >
            <i className="ri-menu-line text-[22px]" aria-hidden />
          </button>
        </div>
      </div>
    </header>
  );
}